import {CM_LOGGER_RENDERING} from "@app/common/services/logging"
import {
    AfterViewInit,
    ChangeDetectionStrategy,
    Component,
    computed,
    DestroyRef,
    effect,
    ElementRef,
    EventEmitter,
    inject,
    input,
    OnDestroy,
    output,
    signal,
    viewChild,
} from "@angular/core"
import {WebGl2CanvasComponent} from "@common/components/canvas/webgl2-canvas/webgl2-canvas.component"
import {CanvasNavigationLegacy} from "@common/helpers/canvas/canvas-navigation-legacy"
import {HalContext, HalImageRef} from "@cm/hal"
import {HalPainterImageStretch} from "@app/common/components/canvas/webgl2-canvas/hal-painter-image-stretch"
import * as paper from "paper"
import {fromEvent, map, merge, Observable, Subject, Subscription, switchMap, take, takeUntil} from "rxjs"
import {Box2, Box2Like, Matrix3x2, Vector2, Vector2Like} from "@cm/math"
import {cancelDeferredTask} from "@cm/browser-utils"
import {CanvasBaseToolboxRootItem} from "@common/helpers/canvas/canvas-base-toolbox-legacy/canvas-base-toolbox-root-item"
import {Hotkeys} from "@common/services/hotkeys/hotkeys.service"
import {outputToObservable, takeUntilDestroyed, toObservable} from "@angular/core/rxjs-interop"
import {RulerComponent} from "@common/components/ruler/ruler.component"
import {CanvasBaseToolboxItem, ToolKeyEvent, ToolMouseEvent, ToolWheelEvent} from "@common/helpers/canvas/canvas-base-toolbox-legacy/canvas-base-toolbox-item"
import {ViewMode} from "@app/textures/texture-editor/operator-stack/operators/tiling/toolbox/tiling-area/spatial-mapping-item"
import {MatMenuModule} from "@angular/material/menu"
import {NgClass} from "@angular/common"
import {MatDividerModule} from "@angular/material/divider"
import {assertNever} from "@cm/utils"
import {WebGl2CreateArgs} from "@cm/webgl2"

@Component({
    changeDetection: ChangeDetectionStrategy.OnPush,
    selector: "cm-canvas-base",
    templateUrl: "./canvas-base.component.html",
    styleUrls: ["./canvas-base.component.scss"],
    imports: [WebGl2CanvasComponent, RulerComponent, MatMenuModule, MatDividerModule, NgClass],
})
export class CanvasBaseComponent implements AfterViewInit, OnDestroy {
    readonly logger = inject(CM_LOGGER_RENDERING)

    readonly $canvasElementRef = viewChild.required<ElementRef>("canvasElement")
    readonly $webGlCanvasComponent = viewChild.required<WebGl2CanvasComponent>("webGlCanvas")
    readonly $rulerXComponent = viewChild<RulerComponent>("rulerX")
    readonly $rulerYComponent = viewChild<RulerComponent>("rulerY")
    readonly $suspendRedraws = input(false, {alias: "suspendRedraws"})

    readonly $webglSettings = input<WebGl2CreateArgs>(
        {
            physicalCache: {
                sizeInBytes: 2 * 1024 * 1024 * 1024, // 2GB physical cache size
            },
            virtualCache: undefined, // disabled virtualization
        },
        {alias: "webglSettings"},
    )

    readonly $webgl2CanvasAboutToBeDestroyed = output<void>({alias: "webgl2CanvasAboutToBeDestroyed"})

    readonly $imageUrl = input<string | null>(null, {alias: "imageUrl"})

    readonly $imageData = input<ImageData | undefined>(undefined, {alias: "imageData"})

    readonly $zoomToFitOnLoadingComplete = input(false, {alias: "zoomToFitOnLoadingComplete"})

    readonly $showRulers = input(false, {alias: "showRulers"})

    readonly $physicalInfo = input<CanvasPhysicalInfo | undefined>(undefined, {alias: "physicalInfo"})

    private _physicalInfo: CanvasPhysicalInfo | undefined

    get physicalInfo(): CanvasPhysicalInfo {
        if (!this._physicalInfo) {
            throw Error("Attempting to retrieve physical-info which has never been set.")
        }
        return this._physicalInfo
    }

    setPhysicalInfo(value: CanvasPhysicalInfo): void {
        this._physicalInfo = value
        if (this._navigation) {
            this._navigation.physicalInfo = value
        }
        this.physicalInfoChange.emit(value)
    }

    readonly loadingComplete = output<void>()
    readonly loadingError = output<void>()
    readonly canvasBoundsChange = new EventEmitter<Box2>()
    readonly physicalInfoChange = new EventEmitter<CanvasPhysicalInfo | undefined>()
    readonly frameDrawn = new EventEmitter<void>()
    readonly hotkeys = inject(Hotkeys)
    private readonly destroyRef = inject(DestroyRef)

    constructor() {
        effect(() => {
            const url = this.$imageUrl()
            if (this.imageLoadSubscription) {
                this.imageLoadSubscription.unsubscribe()
            }
            if (url) this.imageLoadSubscription = this.loadImage(url, true).pipe(takeUntil(this.destroySubject)).subscribe() // assume sRGB
        })
        effect(() => {
            const value = this.$imageData()
            if (!value) return
            const canvas: HTMLCanvasElement = document.createElement("canvas")
            canvas.width = value.width
            canvas.height = value.height
            canvas.getContext("2d")?.putImageData(value, 0, 0)
            if (this.imageLoadSubscription) {
                this.imageLoadSubscription.unsubscribe()
            }
            this.imageLoadSubscription = this.loadImage(canvas.toDataURL(), true).pipe(takeUntil(this.destroySubject)).subscribe() // assume sRGB
        })
        effect(() => {
            const value = this.$physicalInfo()
            if (value === undefined) {
                return
            }
            this._physicalInfo = value
            if (this._navigation) {
                this._navigation.physicalInfo = value
            }
            this.physicalInfoChange.emit(value)
        })
        toObservable(this.$suspendRedraws)
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe((value) => {
                if (!value && this._newFrameRequestedDuringDraw) {
                    this.requestRedraw()
                }
            })
    }

    ngAfterViewInit() {
        this._canvas = this.$canvasElementRef().nativeElement
        this._paperScope = new paper.PaperScope()
        this._paperScope.setup(this._canvas)
        this._paperProject = this._paperScope.project
        this._paperLayer = this._paperProject.activeLayer
        this._paperLayer.name = "PaperLayer"

        fromEvent<ErrorEvent>(this.image, "error")
            .pipe(takeUntil(this.destroySubject))
            .subscribe((error: ErrorEvent) => {
                this.logger.error("Cannot load image.", error)
                this.loadingError.emit()
            })

        this.$webGlCanvasComponent().resized.subscribe(() => this.drawWebGlCanvas())
        this._halPainterImageStretch = HalPainterImageStretch.create(this.halContext)

        this._paperScope.activate()
        this._navigation = new CanvasNavigationLegacy(this, this._canvas, this._paperProject, this.image)
        this._navigation.viewChange.pipe(takeUntil(this.destroySubject)).subscribe(() => this.onViewChanged())
        this._navigation.cursorPosChange.pipe(takeUntil(this.destroySubject)).subscribe(() => this.onCursorPosChanged())
        this._navigation.physicalInfo = this._physicalInfo
        this._navigation.zoomTo(1)
        this.$navigationReady.set(true)
    }

    protected webgl2CanvasAboutToBeDestroyed() {
        this.$webgl2CanvasAboutToBeDestroyed.emit()

        this._destroyed = true

        if (this._redrawRequestId != null) {
            cancelDeferredTask(this._redrawRequestId)
            this._redrawRequestId = null
        }

        this._halPainterImageStretch?.dispose()
        this._halPainterImageStretch = null
        this._halImage?.release()
        this._halImage = null

        this._paperProject.clear()
        this._paperProject.remove()
        this.destroySubject.next()
        this.destroySubject.complete()
        this._navigation.destroy()
    }

    ngOnDestroy(): void {}

    get destroyed(): boolean {
        return this._destroyed
    }

    get halContext(): HalContext {
        return this.$webGlCanvasComponent().halContext
    }

    get navigation(): CanvasNavigationLegacy {
        return this._navigation
    }

    get paperScope(): paper.PaperScope {
        return this._paperScope
    }

    get paperLayer(): paper.Layer {
        return this._paperLayer
    }

    get image(): HTMLImageElement {
        return this._image
    }

    get halImage(): HalImageRef | null {
        return this._halImage
    }

    get toolbox(): CanvasBaseToolboxRootItem | null {
        return this._navigation.toolboxRootItem
    }

    set toolbox(canvasToolbox: CanvasBaseToolboxRootItem | null) {
        this._navigation.toolboxRootItem = canvasToolbox
    }

    get canvasBounds(): Box2 {
        return this._canvasBounds
    }

    get canvasCursorPosition(): Vector2 {
        return this._navigation.canvasCursorPosition
    }

    get customDrawFn(): CustomDrawFn | null {
        return this._customDrawFn
    }

    set customDrawFn(value: CustomDrawFn | null) {
        this._customDrawFn = value
        this.requestRedraw()
    }

    get isSRGB(): boolean {
        return this._imageIsSrgb
    }

    set isSRGB(value: boolean) {
        this._imageIsSrgb = value
    }

    get displayGamma(): number {
        return this._displayGamma
    }

    set displayGamma(value: number) {
        this._displayGamma = value
    }

    private onViewChanged() {
        this.requestRedraw()
        this.updateRulers()
    }

    private onCursorPosChanged() {
        this.updateRulers()
    }

    private updateRulers() {
        this.$rulerXComponent()?.update(this.$rulerXOriginUnitOffset(), this.$rulerCursorPositionX())
        this.$rulerYComponent()?.update(this.$rulerYOriginUnitOffset(), this.$rulerCursorPositionY())
    }

    loadImage(url: string, isSRGB: boolean): Observable<void> {
        this.isSRGB = isSRGB
        this._image.crossOrigin = "anonymous"
        const loadObservable = fromEvent(this._image, "load").pipe(
            take(1),
            switchMap(async () => {
                await this.showOriginalImage()
                if (this.$zoomToFitOnLoadingComplete()) {
                    this._navigation.zoomToFitImage()
                }
                this.loadingComplete.emit()
            }),
        )
        this._image.src = url
        return merge(
            loadObservable,
            outputToObservable(this.loadingError).pipe(
                take(1),
                map(() => {
                    throw new Error("Failed to load image")
                }),
            ),
        )
    }

    async showOriginalImage() {
        await this.showImage(this._image, this._imageIsSrgb)
    }

    async showImage(image: HTMLImageElement, isSrgb: boolean): Promise<void> {
        this._halImage?.release()
        this._halImage = this.halContext.createPhysicalImage({
            width: image.width,
            height: image.height,
            channelLayout: "RGBA",
            dataType: isSrgb ? "uint8norm_srgb" : "uint8norm",
        })
        await this._halImage.ref.writeImageData({
            sourceData: {
                isSrgb: isSrgb,
                data: image,
            },
        })
        await this.drawWebGlCanvas()
    }

    async showCanvas(canvas: HTMLCanvasElement, isSrgb: boolean): Promise<void> {
        this._halImage?.release()
        this._halImage = this.halContext.createPhysicalImage({
            width: canvas.width,
            height: canvas.height,
            channelLayout: "RGBA",
            dataType: isSrgb ? "uint8norm_srgb" : "uint8norm",
        })
        await this._halImage.ref.writeImageData({
            sourceData: {
                isSrgb: isSrgb,
                data: canvas,
            },
        })
        await this.drawWebGlCanvas()
    }

    get isRedrawRequested(): boolean {
        return this._redrawRequestId != null
    }

    requestRedraw() {
        if (this._destroyed) {
            return
        }
        if (!this.$suspendRedraws() && this._redrawRequestId == null) {
            //this._redrawRequestId = queueDeferredTask(() => this.drawWebGlCanvas())
            this._redrawRequestId = requestAnimationFrame(() => this.drawWebGlCanvas())
        } else {
            this._newFrameRequestedDuringDraw = true
        }
    }

    // TODO on macOS the reported resolution is sometimes altered by the OS so that the devicePixelRatio is integer to avoid blurry rendering
    // TODO though this can cause excessive over-sampling resolution so the max resolution is limited to 4K (in webgl2-canvas.component.ts)
    // TODO however when mapping the view coordinates through the inverse view transform, it is still too large i think - to be further investigated !
    get devicePixelRatio(): number {
        return devicePixelRatio
        // return this.$webGlCanvasComponent().devicePixelRatio
    }

    get viewTransform(): paper.Matrix {
        const devicePixelRatio = this.$webGlCanvasComponent().devicePixelRatio
        const dpiCorrectedTransform = new paper.Matrix().scale(devicePixelRatio, devicePixelRatio)
        dpiCorrectedTransform.append(this._paperProject.view.matrix)
        return dpiCorrectedTransform
    }

    totalHack: (() => Promise<void>) | null = null

    private async drawWebGlCanvas(): Promise<void> {
        if (this.totalHack) {
            this._redrawRequestId = null
            await this.totalHack()
            this.frameDrawn.emit()
            if (this._newFrameRequestedDuringDraw) {
                this._newFrameRequestedDuringDraw = false
                this.requestRedraw()
            }
            return
        }
        const start = performance.now()
        try {
            this.$webGlCanvasComponent().clearBackBuffer()
            const drawFn = this._customDrawFn ?? this.defaultDrawFn.bind(this)
            const imageBounds = await drawFn()
            this.setCanvasBounds(imageBounds)
            await this.$webGlCanvasComponent().presentBackBuffer(this._imageIsSrgb, this._displayGamma)
        } finally {
            this._redrawRequestId = null
            this.logger.debug("Canvas draw time: ", performance.now() - start)
            this.frameDrawn.emit()
            if (this._newFrameRequestedDuringDraw) {
                this._newFrameRequestedDuringDraw = false
                this.requestRedraw()
            }
        }
    }

    private async defaultDrawFn(): Promise<Box2Like> {
        if (!this._halPainterImageStretch) {
            throw new Error("CanvasBaseComponent: HalPainterImageStretch not initialized")
        }
        if (!this.halImage) {
            return {x: 0, y: 0, width: 0, height: 0}
        }
        this._halPainterImageStretch.paint(this.$webGlCanvasComponent().backBufferImage.ref, this.halImage.ref, {transform: this.viewTransform})
        return {x: 0, y: 0, width: this.halImage.ref.descriptor.width, height: this.halImage.ref.descriptor.height}
    }

    setCanvasBounds(boundingBox: Box2Like) {
        if (this._canvasBounds.equals(boundingBox)) {
            return
        }
        this._canvasBounds = Box2.fromBox2Like(boundingBox)
        this.canvasBoundsChange.emit(this._canvasBounds)
    }

    readonly $measurementUnitOptions = computed((): MeasurementUnit[] => ["px", "mm", "cm", "in"])

    readonly $measurementUnit = signal<MeasurementUnit>("cm")
    readonly $navigationReady = signal(false)

    readonly $zoomLevel = computed(() => (this.$navigationReady() ? this._navigation.$zoomLevel() : 1))

    get measurementUnit(): MeasurementUnit {
        return this.$measurementUnit()
    }

    set measurementUnit(value: MeasurementUnit) {
        this.$measurementUnit.set(value)
    }

    readonly $rulerPixelsPerUnit = computed(() => {
        if (!this.$navigationReady()) return 1
        return this._navigation.$zoomLevel() * this.pixelsPerUnit(this.$measurementUnit())
    })

    readonly $rulerXOriginUnitOffset = computed(() => {
        if (!this.$navigationReady()) return 0
        return this._navigation.$viewTopLeft().x / this.pixelsPerUnit(this.$measurementUnit())
    })

    readonly $rulerYOriginUnitOffset = computed(() => {
        if (!this.$navigationReady()) return 0
        return this._navigation.$viewTopLeft().y / this.pixelsPerUnit(this.$measurementUnit())
    })

    readonly $rulerCursorPositionX = computed(() => {
        if (!this.$navigationReady()) return 0
        return this._navigation.$cursorPosition().x / this.pixelsPerUnit(this.$measurementUnit())
    })

    readonly $rulerCursorPositionY = computed(() => {
        if (!this.$navigationReady()) return 0
        return this._navigation.$cursorPosition().y / this.pixelsPerUnit(this.$measurementUnit())
    })

    private pixelsPerUnit(unit: MeasurementUnit): number {
        switch (unit) {
            case "px":
                return 1
            case "mm":
                return this.physicalInfo.pixelsPerCm / 10
            case "cm":
                return this.physicalInfo.pixelsPerCm
            case "in":
                return this.physicalInfo.pixelsPerCm * 2.54
            default:
                assertNever(unit)
        }
    }

    private _redrawRequestId: number | null = null
    private _newFrameRequestedDuringDraw = false
    private _customDrawFn: CustomDrawFn | null = null
    private _halPainterImageStretch: HalPainterImageStretch | null = null
    private _navigation!: CanvasNavigationLegacy
    private imageLoadSubscription!: Subscription
    private destroySubject = new Subject<void>()
    private _canvas!: HTMLCanvasElement
    private _image: HTMLImageElement = new Image()
    private _imageIsSrgb = false
    private _displayGamma = 1
    private _halImage: HalImageRef | null = null
    private _canvasBounds: Box2 = new Box2(0, 0, 0, 0)

    private _paperScope!: paper.PaperScope
    private _paperProject!: paper.Project
    private _paperLayer!: paper.Layer

    private _destroyed = false

    readonly $showCursorLabel = signal(false)

    // CanvasBaseToolboxItem dummy implementation (only beginPaperCreation is used)
    // TODO clen this up (probably by making the canvas-navigation the actual base item)
    get canvasBase(): CanvasBaseComponent {
        return this
    }

    readonly viewChange = new EventEmitter<Matrix3x2>()
    readonly cursorChange = new EventEmitter<string | undefined>()

    visible = true
    selected = false
    readonly $cursor = signal<string | undefined>(undefined)

    get cursor(): string | undefined {
        return this.$cursor()
    }

    set cursor(value: string | undefined) {
        this.$cursor.set(value)
    }

    get children(): readonly CanvasBaseToolboxItem[] {
        return []
    }

    remove(): void {}

    hitTest(_point: Vector2Like): CanvasBaseToolboxItem | null {
        return null
    }

    isPointInImage(point: paper.Point): boolean {
        return this.canvasBounds.containsPoint(point)
    }

    beginPaperCreation(): void {
        this._paperScope.activate()
        this._paperLayer.activate()
    }

    sendToBack(): void {}

    bringToFront(): void {}

    sendItemToBack(_item: CanvasBaseToolboxItem): void {}

    bringItemToFront(_item: CanvasBaseToolboxItem): void {}

    onKeyDown(_event: ToolKeyEvent): boolean {
        return true
    }

    onKeyUp(_event: ToolKeyEvent): boolean {
        return true
    }

    onMouseWheel(_event: ToolWheelEvent): boolean {
        return true
    }

    onMouseDown(_event: ToolMouseEvent): boolean {
        return true
    }

    onMouseUp(_event: ToolMouseEvent): boolean {
        return true
    }

    onMouseDrag(_event: ToolMouseEvent): boolean {
        return true
    }

    onMouseMove(_event: ToolMouseEvent): boolean {
        return true
    }

    onMouseLeave(_event: ToolMouseEvent): boolean {
        return true
    }

    addChildItem(_item: CanvasBaseToolboxItem): void {}

    protected readonly ViewMode = ViewMode
}

type CustomDrawFn = () => Promise<Box2Like> // returns the box of the drawn image

export type CanvasPhysicalInfo = {
    pixelsPerCm: number
    originXCm: number
    originYCm: number
}

export type MeasurementUnit = "px" | "mm" | "cm" | "in"
